import React, { useRef, useState } from 'react';
import { MapPin, Loader2, Search, AlertTriangle, ShieldCheck } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { C, confidenceStyle } from '../shared';

export const AddressStep = ({ api, addr, onResolved }) => {
  const [q, setQ] = useState(addr?.label || '');
  const [sugg, setSugg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  const suggest = text => {
    clearTimeout(timer.current);
    if (text.trim().length < 3) { setSugg(null); return; }
    timer.current = setTimeout(async () => {
      try { const { data } = await api.get('/property/address/suggest', { params: { q: text } }); setSugg(data); }
      catch (err) { console.error('API error:', err?.message); }
    }, 350);
  };

  const resolve = async text => {
    setBusy(true); setError(null); setSugg(null);
    try {
      const { data } = await api.get('/property/address/resolve', { params: { q: text } });
      onResolved(data);
    } catch (err) { setError(err?.response?.data?.detail || err?.message || 'resolve failed'); }
    setBusy(false);
  };

  return (
    <div data-testid="rj-address-step">
      <p className="text-xs mb-2" style={{ color: C.slate }}>Type the risk address. It resolves on the state register (NSW · VIC · QLD · TAS) and everything the registers know prefills the next steps.</p>
      <div className="relative">
        <div className="flex gap-2">
          <Input value={q} onChange={e => { setQ(e.target.value); suggest(e.target.value); }} onKeyDown={e => e.key === 'Enter' && q.trim() && resolve(q)}
            placeholder="e.g. 3 Dorothy Street Ryde NSW 2112" className="rounded-sm text-sm" data-testid="rj-address-input" />
          <Button onClick={() => resolve(q)} disabled={busy || q.trim().length < 3} className="rounded-sm" style={{ background: C.blue }} data-testid="rj-address-resolve-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}<span className="ml-1.5 text-xs">Resolve</span>
          </Button>
        </div>
        {sugg?.suggestions?.length > 0 && (
          <ul className="absolute z-20 left-0 right-24 mt-1 bg-white border border-[rgba(136,150,168,0.25)] rounded-sm shadow-lg max-h-56 overflow-auto" data-testid="rj-address-suggestions">
            {sugg.suggestions.map(s => (
              <li key={s.pid}>
                <button type="button" onClick={() => { setQ(s.label); resolve(s.label); }} className="w-full text-left px-3 py-2 text-xs hover:bg-[#F5F7FA] flex justify-between gap-2" data-testid={`rj-suggestion-${s.pid}`}>
                  <span style={{ color: C.navy }}>{s.label}</span><span className="font-mono text-[10px] shrink-0" style={{ color: C.slate }}>{s.pid}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {error && <p className="mt-3 text-xs flex items-center gap-1.5" style={{ color: C.red }} data-testid="rj-address-error"><AlertTriangle className="w-3.5 h-3.5" /> {String(error)}</p>}
      {addr && (
        <div className="mt-3 rounded-sm border p-3" style={{ borderColor: 'rgba(29,158,117,0.4)', background: 'rgba(29,158,117,0.04)' }} data-testid="rj-address-resolved">
          <p className="text-sm font-semibold flex items-center gap-2" style={{ color: C.navy }}><MapPin className="w-4 h-4" style={{ color: C.green }} /> {addr.label}</p>
          <p className="mt-1 flex flex-wrap gap-1.5 items-center text-[10px]">
            <span className="font-mono px-1.5 py-0.5 rounded" style={{ background: C.bg, color: C.navy }} data-testid="rj-address-pid">{addr.pid}</span>
            <span className="font-semibold px-1.5 py-0.5 rounded uppercase" style={confidenceStyle(addr.confidence)}>{addr.confidence}</span>
            <span style={{ color: C.slate }}>{addr.source} · {addr.licence}</span>
            <span className="flex items-center gap-1" style={{ color: C.green }}><ShieldCheck className="w-3 h-3" /> {addr.elapsed_ms ? `${addr.elapsed_ms} ms` : 'PID cache'}</span>
          </p>
        </div>
      )}
    </div>
  );
};
